import React, { useState, useEffect } from "react";
import {
  Link,
  Route,
  Routes,
  useNavigate,
  useLocation,
} from "react-router-dom";
import CreateRestaurant from "./CreateRestaurant";
import EditRestaurant from "./EditRestaurant";
import UserProfile from "./UserProfile";
import PlaceOrder from "./PlaceOrder";
import OrderSummary from "./OrderSummary";
import QRCodeScanning from "./QRCodeScanning";
import MenuManagement from "./MenuManagement";
import OrderManagement from "./OrderManagement";
import KitchenManagement from "./KitchenManagement";
import AuthGuard from "../guard/AuthGuard";
import axios from "axios";
import config from "../config";
import apiClient from "../api/ResturantClient";
import LoadingSpinner from "./LoadingSpinner";

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [hasRestaurant, setHasRestaurant] = useState(false);
  const [restaurant, setRestaurant] = useState({});
  const [selectedMenuItem, setSelectedMenuItem] = useState("");
  const [message, setMessage] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    fetchRestaurantStatus();
  }, []);

  const fetchRestaurantStatus = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("RestaurantToken");

      // Check if user is authenticated FIRST
      if (!token) {
        // Clear any stale data and redirect
        sessionStorage.clear();
        localStorage.removeItem("RestaurantToken");
        window.location.href = "/";
        return;
      }

      const userId = sessionStorage.getItem("user_id");

      // Validate userId before making API call
      if (!userId || isNaN(parseInt(userId))) {
        console.error("Invalid user ID:", userId);
        sessionStorage.clear();
        localStorage.removeItem("RestaurantToken");
        window.location.href = "/";
        return;
      }

      const parsedUserId = parseInt(userId);

      // Fetch subscription status only if we have a valid user ID
      try {
        const subResponse = await apiClient.get(
          `/subscription/subscription-status/${parsedUserId}`
        );
        setSubscription(subResponse.data);
      } catch (error) {
        console.error("Error fetching subscription:", error);
        // Don't redirect on subscription error - it might be expected for new users
      }

      const restaurantId = sessionStorage.getItem("restaurant_id");

      // Rest of your existing restaurant fetching logic...
      if (!restaurantId) {
        setHasRestaurant(false);
        setSelectedMenuItem("create-restaurant");
        navigate("create-restaurant");
        setIsLoading(false);
        return;
      }

      const response = await axios.get(
        `${config.API_BASE_URL}/restaurant/get-restaurant/${restaurantId}`
      );

      if (response.data && response.data.data) {
        const isRestaurant = response.data.status || false;

        sessionStorage.setItem(
          "restaurant_id",
          response.data.data.restaurant_id
        );
        setRestaurant(response.data.data);
        setHasRestaurant(isRestaurant);

        if (!isRestaurant) {
          setSelectedMenuItem("create-restaurant");
          navigate("create-restaurant");
        } else {
          const currentPath = location.pathname.split("/")[2];
          if (!currentPath) {
            setSelectedMenuItem("order-summary");
            navigate("order-summary");
          } else {
            setSelectedMenuItem(currentPath);
          }
        }
      } else {
        setHasRestaurant(false);
        setSelectedMenuItem("create-restaurant");
        navigate("create-restaurant");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      // Check if it's an authentication error
      if (error.response?.status === 401 || error.response?.status === 403) {
        sessionStorage.clear();
        localStorage.removeItem("RestaurantToken");
        window.location.href = "/";
        return;
      }
      setHasRestaurant(false);
      setSelectedMenuItem("create-restaurant");
      navigate("create-restaurant");
    } finally {
      setIsLoading(false);
    }
  };

  // Add these utility functions near the top of your Dashboard component
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const isEndDateWithin5Days = (subscription) => {
    if (
      !subscription ||
      !subscription.end_date ||
      subscription.status !== "active"
    ) {
      return false;
    }

    const endDate = new Date(subscription.end_date);
    const today = new Date();
    const timeDiff = endDate - today;
    const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

    return daysDiff <= 5 && daysDiff >= 0;
  };

  const getDaysUntilEndDate = (subscription) => {
    if (!subscription || !subscription.end_date) return 0;

    const endDate = new Date(subscription.end_date);
    const today = new Date();
    const timeDiff = endDate - today;
    return Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
  };

  const getGracePeriodDaysInfo = (subscription) => {
    if (
      !subscription ||
      !subscription.end_date ||
      !subscription.is_in_grace_period
    ) {
      return null;
    }

    const now = new Date();
    const endDate = new Date(subscription.end_date);
    const gracePeriodEndDate = new Date(subscription.grace_period_end_date);

    const totalGraceDays = Math.ceil(
      (gracePeriodEndDate - endDate) / (1000 * 60 * 60 * 24)
    );
    const currentGraceDay =
      totalGraceDays -
      Math.ceil((gracePeriodEndDate - now) / (1000 * 60 * 60 * 24));
    const remainingDays = Math.ceil(
      (gracePeriodEndDate - now) / (1000 * 60 * 60 * 24)
    );

    return {
      currentDay: currentGraceDay,
      remainingDays: remainingDays,
      totalGraceDays: totalGraceDays,
    };
  };

  const handleLogout = async () => {
    // Clear all auth tokens and state
    sessionStorage.clear();
    localStorage.removeItem("RestaurantToken");
    localStorage.clear();

    // Force a complete navigation (not relative)
    window.location.replace("/"); // This causes a full page reload
  };

  const handleMenuItemClick = (path) => {
    setSelectedMenuItem(path);
    navigate(path);
    setIsMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Component to render subscription alerts
  const SubscriptionAlerts = () => {
    if (!subscription) return null;

    return (
      <div className="w-full space-y-4 mb-6">
        {/* Subscription Ending Soon Alert */}
        {isEndDateWithin5Days(subscription) &&
          subscription.status === "active" && (
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-l-4 border-orange-500 p-4 rounded-lg shadow-sm">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-orange-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-orange-800">
                    <span className="font-medium">
                      Subscription Ending Soon:
                    </span>{" "}
                    Your subscription will expire in{" "}
                    {getDaysUntilEndDate(subscription)} day
                    {getDaysUntilEndDate(subscription) !== 1 ? "s" : ""} on{" "}
                    {formatDate(subscription.end_date)}.
                  </p>
                </div>
              </div>
            </div>
          )}

        {/* Grace Period Alert */}
        {subscription.status === "grace_period" && (
          <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-l-4 border-yellow-500 p-4 rounded-lg shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-yellow-500"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-800">
                  <span className="font-medium">Grace Period:</span> You're on
                  day {getGracePeriodDaysInfo(subscription)?.currentDay} of{" "}
                  {getGracePeriodDaysInfo(subscription)?.totalGraceDays}. Your
                  access will expire in{" "}
                  {getGracePeriodDaysInfo(subscription)?.remainingDays} day
                  {getGracePeriodDaysInfo(subscription)?.remainingDays !== 1
                    ? "s"
                    : ""}
                  .
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="flex w-full max-w-7xl mx-auto p-4 bg-gradient-to-br from-gray-50 to-blue-50/30 rounded-2xl shadow-xl min-h-screen">
      {/* Mobile Menu Button */}
      <button
        className={`fixed top-20 left-6 z-50 p-3 rounded-xl bg-white/90 backdrop-blur-sm shadow-lg md:hidden transition-all duration-300 ${
          isMenuOpen ? "bg-gray-100 rotate-90" : "hover:bg-gray-50"
        }`}
        onClick={toggleMenu}
      >
        <div className="w-6 h-0.5 bg-gray-800 mb-1.5 transition-transform"></div>
        <div className="w-6 h-0.5 bg-gray-800 mb-1.5 transition-transform"></div>
        <div className="w-6 h-0.5 bg-gray-800 transition-transform"></div>
      </button>

      {/* Sidebar */}
      <div
        className={`
          fixed md:sticky md:top-0 md:left-0 md:h-screen md:w-72 bg-white/95 backdrop-blur-sm shadow-2xl transition-all duration-300 ease-in-out z-40
          ${isMenuOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full"}
          md:translate-x-0 md:mr-8 p-8 rounded-2xl h-full border border-gray-200/60
        `}
      >
        {/* Header */}
        <div className="mb-10">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Dashboard
          </h2>
          {restaurant.name && (
            <p className="text-sm text-gray-600 mt-2 font-medium truncate">
              {restaurant.name}
            </p>
          )}
        </div>

        {message && (
          <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">{message}</p>
          </div>
        )}

        {/* Navigation */}
        <nav className="space-y-3">
          <div
            onClick={() => handleMenuItemClick("order-summary")}
            className={`block px-5 py-4 rounded-xl transition-all cursor-pointer border border-transparent ${
              selectedMenuItem === "order-summary"
                ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg shadow-blue-500/25 border-blue-400"
                : "hover:bg-gray-50 hover:border-gray-200 text-gray-700 hover:shadow-md"
            } ${!hasRestaurant ? "opacity-50 cursor-not-allowed" : ""}`}
            style={{ pointerEvents: hasRestaurant ? "auto" : "none" }}
          >
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-current rounded-full opacity-70"></div>
              <span className="font-medium">Order Summary</span>
            </div>
          </div>

          {hasRestaurant ? (
            <div
              onClick={() => handleMenuItemClick("edit-restaurant")}
              className={`block px-5 py-4 rounded-xl transition-all cursor-pointer border border-transparent ${
                selectedMenuItem === "edit-restaurant"
                  ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg shadow-blue-500/25 border-blue-400"
                  : "hover:bg-gray-50 hover:border-gray-200 text-gray-700 hover:shadow-md"
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-current rounded-full opacity-70"></div>
                <span className="font-medium">Edit Restaurant</span>
              </div>
            </div>
          ) : (
            <div
              onClick={() => handleMenuItemClick("create-restaurant")}
              className={`block px-5 py-4 rounded-xl transition-all cursor-pointer border border-transparent ${
                selectedMenuItem === "create-restaurant"
                  ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg shadow-blue-500/25 border-blue-400"
                  : "hover:bg-gray-50 hover:border-gray-200 text-gray-700 hover:shadow-md"
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-current rounded-full opacity-70"></div>
                <span className="font-medium">Create Restaurant</span>
              </div>
            </div>
          )}

          {[
            "profile",
            "generate-qr-code",
            "menu-management",
            "order-management",
            "kitchen-management",
          ].map((item) => (
            <div
              key={item}
              onClick={() => handleMenuItemClick(item)}
              className={`block px-5 py-4 rounded-xl transition-all cursor-pointer border border-transparent ${
                selectedMenuItem === item
                  ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg shadow-blue-500/25 border-blue-400"
                  : "hover:bg-gray-50 hover:border-gray-200 text-gray-700 hover:shadow-md"
              } ${!hasRestaurant ? "opacity-50 cursor-not-allowed" : ""}`}
              style={{ pointerEvents: hasRestaurant ? "auto" : "none" }}
            >
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-current rounded-full opacity-70"></div>
                <span className="font-medium">
                  {item
                    .split("-")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")}
                </span>
              </div>
            </div>
          ))}
        </nav>

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className="w-full mt-6 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white py-4 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-[1.02] active:scale-95 border border-red-400/30"
        >
          Log Out
        </button>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 md:ml-8 mt-20 md:mt-0">
        {/* Subscription Alerts */}
        <SubscriptionAlerts />

        {/* Routes */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/60 p-2">
          <Routes>
            <Route
              path="/"
              element={<AuthGuard component={<OrderSummary />} />}
            />
            {hasRestaurant ? (
              <Route
                path="edit-restaurant"
                element={<AuthGuard component={<EditRestaurant />} />}
              />
            ) : (
              <Route
                path="create-restaurant"
                element={<AuthGuard component={<CreateRestaurant />} />}
              />
            )}
            <Route
              path="profile"
              element={<AuthGuard component={<UserProfile />} />}
            />
            <Route
              path="place-order"
              element={<AuthGuard component={<PlaceOrder />} />}
            />
            <Route
              path="order-summary"
              element={<AuthGuard component={<OrderSummary />} />}
            />
            <Route
              path="generate-qr-code"
              element={<AuthGuard component={<QRCodeScanning />} />}
            />
            <Route
              path="menu-management"
              element={<AuthGuard component={<MenuManagement />} />}
            />
            <Route
              path="order-management"
              element={<AuthGuard component={<OrderManagement />} />}
            />
            <Route
              path="kitchen-management"
              element={<AuthGuard component={<KitchenManagement />} />}
            />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;